/**
 * Responsible for performing form validation via the Constraint Validation API.
 * It provides both custom data attributes as well leverage any fields in a
 * given area (form or any container with elements) that are seen as invalid.
 * @module elements/fields/validator
 */

import $ from 'jquery';
import kebabCase from 'lodash/kebabCase';
import partial from 'lodash/partial';
import get from 'lodash/get';
import formScroller from './form-error-scroller';
import registerJQueryPlugin, { noopActions } from '../../lib/register-jquery-plugin';
import { makeValidateMissing } from './validate-missing';
import { validateSameAs } from './validate-same-as';
import { validateEmail } from './validate-email';
import { DEFAULT_MESSAGE } from './validator-constants';

export { DEFAULT_MESSAGE };

const PLUGIN_NAME = 'validator';
const { placeholder } = partial;

/**
 * Defines default options for a validator.
 * @type {Object}
 */
const VALIDATOR_OPTIONS = {
  /**
   * Allows for a form to be validated only upon submission.
   */
  validateOnlyOnSubmit: false,
  /**
   * Called when a submission is valid
   * @param {Array} validElements - An array of valid HTML elements
   */
  successCallback: validElements => validElements,
  /**
   * Called when a submission was unsuccessful
   * @param {Array} invalidElements - An array of invalid HTML elements
   */
  errorCallback: invalidElements => invalidElements,
  /**
   * Provides baseline implementation of where/how the page should scroll to
   * when an error has been found.
   * @param {number} x - The x coordinate to scroll to
   * @param {number} y - The y coordinate to scroll to
   */
  scrollTrigger: (x, y) => window.scrollTo(x, y),
  /**
   * Allows for custom scrolling when an error occurs.
   */
  useCustomFormErrorScroll: formScroller,
};

/**
 * Defines all validator related events.
 * @type {Object}
 */
export const EVENTS = {
  INVALID_SCROLL_POSITION: 'validator:invalid-scroll-position',
  VALIDATOR_SUBMIT: 'validator:submit',
  VALIDATOR_SUCCESS: 'validator:success',
  VALIDATOR_ERROR: 'validator:error',
  VALIDATOR_POST_SUCCESS: 'validator:post-success',
  VALIDATOR_POST_ERROR: 'validator:post-error',
};

export const EVENT_NS = 'validator';

/**
 * Defines the data attribute for triggering an element.
 * @type {string}
 */
export const VALIDATE_TRIGGER = 'data-validate-trigger';

/**
 * Defines a number of custom data attributes used for validating.
 * @private
 * @type {Object}
 */
const validators = {
  /**
   * Ensures that an element is not empty. This is different from a required
   * attribute as the validation occurs on submit by the validator verses
   * natively in the browser.
   *
   * @example
   * <form>
   *   <input type="text" data-validate-not-empty="true" />
   *   <button>Submit</button>
   * </form>
   *
   * @param {jQuery} $el - Elements checked to be not empty
   * @returns {string} Value used for setting custom validity
   */
  validateNotEmpty: makeValidateMissing('validateNotEmpty'),

  /**
   * Ensures that an element has a value or that is required. This is different
   * from a required attribute as the validation occurs on submit by the
   * validator verses natively in the browser.
   *
   * @example
   * <form>
   *   <input type="text" data-validate-required="true" />
   *   <button>Submit</button>
   * </form>
   *
   * @param {jQuery} $el - Elements checked to be required or have a value
   * @returns {string} Value used for setting custom validity
   */
  validateRequired: makeValidateMissing('validateRequired'),

  /**
   * Ensure that an element value is not set to the provided value. When it has
   * been set to the value provided, the element will be set as invalid.
   *
   * @example
   * <form>
   *   <input type="text" data-validate-not="walter" />
   *   <button>Submit</button>
   * </form>
   *
   * @param {jQuery} $el - Elements checked to be not empty
   * @param {string} dataVal - Elements checked to be not empty
   * @returns {string} Value used for setting custom validity
   */
  validateNot($el, dataVal) {
    if ($el.val() === dataVal) {
      return $el.data('errorMessage') || DEFAULT_MESSAGE;
    }

    return '';
  },

  validateEmail,

  validateSameAs,
};

const createEvent = name => `${name}.${EVENT_NS}`;

/**
 * Simple helper that will determine the top position of an element on the page.
 * @private
 * @param {HTMLElement} el - Element under question to find top position
 * @returns {number} The total distance from the top for an element
 */
const getTop = (el) => {
  const bodyTop = document.body.getBoundingClientRect().top;
  const elTop = el.getBoundingClientRect().top;
  return elTop - bodyTop;
};

/**
 * Figures out the highest elements found in the DOM based on an array of HTML
 * elements.
 * @private
 * @param {Array} invalidElements - A list of HTML elements
 * @returns {HTMLElement} Highest HTML element on the page
 */
const findHighestEl = (invalidElements) => {
  const [initial] = invalidElements;
  return invalidElements.reduce((prev, curr) =>
    (getTop(curr) < getTop(prev) ? curr : prev),
    initial,
  );
};

/**
 * Determines where in the form to scroll to when an error has been found, so
 * that the user has some context about what in the form has been filled in
 * incorrectly.
 * @private
 * @param {Array} invalidElements - A list of invalid elements
 * @param {Function} scrollTrigger - Called on to determine where to scroll to.
 */
const setInvalidScrollPosition = (invalidElements, scrollTrigger) => {
  const highestEl = findHighestEl(invalidElements);
  let yAxis = getTop(highestEl);
  let isDelegatedScroll = false;
  const dict = {
    detail: {
      highestEl,
      setElement: (el) => {
        yAxis = getTop(el);
      },
      setPosition: (pos) => {
        yAxis = window.scrollY + pos;
      },
      setRawPosition: (pos) => {
        yAxis = pos;
      },
      delegateScroll: (fn) => {
        fn(highestEl);
        isDelegatedScroll = true;
      },
    },
  };

  const evt = new CustomEvent(EVENTS.INVALID_SCROLL_POSITION, dict);
  highestEl.dispatchEvent(evt);

  if (!isDelegatedScroll) {
    scrollTrigger(0, yAxis);
  }
};

/**
 * Validates for form field based on whether it contains a custom data
 * attribute as specified by any validators. It's purpose is to set the proper
 * validity if the field's requirements haven't been met. It will return true
 * if the element was found invalid.
 * @private
 * @see validators
 * @param {HTMLSelectElement|HTMLInputElement} el - Form element
 * @returns {boolean|undefined} Determines if element is invalid
 */
const validateField = (el) => {
  const $el = $(el);

  if ($el.is(':hidden')) {
    return false;
  }

  const dataAttrs = $el.data();
  const validatorList = Object.keys(validators);

  for (let i = 0, len = validatorList.length; i < len; i += 1) {
    const name = validatorList[i];
    const dataVal = dataAttrs[name];

    if (!!dataVal && !!validators[name]) {
      const message = validators[name]($el, dataVal, dataAttrs);
      if (message) {
        $el.attr('aria-invalid', true);
        el.setCustomValidity(message);
        return true;
      }

      $el.attr('aria-invalid', false);
      el.setCustomValidity('');
    }
  }

  return false;
};

/**
 * Helper that determines all the elements that contain data validator
 * attributes and applies a function to interact with each element.
 * @private
 * @param {jQuery} $wrapper - Container for the form elements
 * @param {Function} fn - Called and passed along a form element
 */
const forEachFormField = ($wrapper, fn) => {
  const dataSelector = Object
    .keys(validators)
    .map(name => `[data-${kebabCase(name)}]`)
    .join(',');

  $wrapper.find(dataSelector).each(function iterateElement() {
    fn(this);
  });
};

/**
 * Responsible for queries invalid elements in a container.
 * @param {jQuery} $wrapper - Container with form elements
 * @returns {Array} List of invalid elements
 */
const getInvalidElementList = $wrapper =>
  $wrapper
    .find(':invalid')
    .not(':hidden,fieldset')
    .toArray();

/**
 * Responsible for queries valid elements in a container.
 * @param {jQuery} $wrapper - Container with form elements
 * @returns {Array} List of valid elements
 */
const getValidElementList = ($wrapper) => {
  const $validEls = $wrapper.find(':valid');
  const els = $validEls
    .not('button,fieldset,input[type="submit"],input[type="radio"],input[type="checkbox"]')
    .toArray();

  const checkedEls = $validEls
    .filter('input[type="radio"],input[type="checkbox"]')
    .toArray();

  const validCheckedEls = checkedEls.reduce((acc, input) => {
    const valid = get(input, 'validity.valid');
    if (input.checked && valid) {
      return [...acc, input];
    }

    return acc;
  }, []);

  return els.concat(validCheckedEls);
};

/**
 * Responsible for verifying a set of elements.
 * @param {Array} elements - List of elements
 */
const verifyElementsValidity = (elements) => {
  elements.forEach((el) => {
    el.checkValidity();
  });
};

/**
 * Responsible for triggering a native CustomEvent so that native event
 * listeners ((add|remove)EventListener) and external listeners (such as jQuery)
 * can work in unison.
 * @param {jQuery} $el - Wrapper element
 * @param {string} evtName - Name of the event
 * @param {Object} detail - Custom data for the event
 */
const triggerEvent = ($el, evtName, detail) => {
  const errorEvt = new CustomEvent(evtName, { detail });
  $el[0].dispatchEvent(errorEvt);
};

/**
 * Retrieves a value for a form element. This can be different based on the
 * type of the element and conditions around it.
 * @param {jQuery} $el - Element looking for value
 */
const getFormFieldValue = ($el) => {
  const type = $el.prop('type');
  if (type === 'radio') {
    return $el.is(':checked') ? $el.val() : null;
  }

  return $el.val();
};

/**
 * Responsible for taking a list of form elements and creating an object that
 * is keyed by the form element name and with a value of the form element.
 * @param {Array} elements - List of form elements
 * @returns {Object} Keyed form element name by value
 */
const createValuesMap = elements =>
  elements.reduce((acc, el) => {
    const $el = $(el);
    const name = $el.attr('name') || $el.attr('id');
    const value = getFormFieldValue($el);

    if (acc[name]) {
      const values = acc[name].values || [acc[name].value];
      const els = acc[name].els || [acc[name].el];
      const combinedEls = [...els, el];
      const combinedValues = [...values, value];

      return Object.assign({}, acc, {
        [name]: {
          values: combinedValues,
          els: combinedEls,
          $el: $(combinedEls),
        },
      });
    }

    return Object.assign({}, acc, {
      [name]: {
        $el,
        el: $el[0],
        value,
      },
    });
  }, {});

/**
 * Responsible for detecting whether the submission failed or was successful
 * and run through the proper processing/notifying.
 * @param {jQuery} $wrapper - Wrapper element
 * @param {Function} successCallback - Fired off if submission was successful
 * @param {Function} errorCallback - Fired off if submissions was unsuccessful
 * @param {Function} useCustomFormErrorScroll - Called on when an error has
 * been found
 * @param {Function} scrollTrigger - Called on to go a position on the page
 * @param {boolean} disableErrorScroll - Disables a form error from scrolling
 * to the error on the screen.
 * @returns {Function} Called on when the submission should be finalized
 */
const processSubmission = (
  $wrapper,
  successCallback,
  errorCallback,
  useCustomFormErrorScroll,
  scrollTrigger,
  disableErrorScroll,
) => (detail, { success, error }) => {
  const invalidElements = getInvalidElementList($wrapper);
  const validElements = getValidElementList($wrapper);
  const invalidValues = createValuesMap(invalidElements);
  const validValues = createValuesMap(validElements);
  const updatedDetail = Object.assign({}, detail, {
    invalidElements,
    invalidValues,
    validElements,
    validValues,
  });
  verifyElementsValidity(invalidElements);

  if (!invalidElements.length) {
    successCallback(validElements);
    triggerEvent($wrapper, success, updatedDetail);
    return;
  }

  const shouldDisableErrorScroll = () =>
    !disableErrorScroll && !$wrapper.data('validateDisableErrorScroll');

  if (!useCustomFormErrorScroll($wrapper, scrollTrigger) && shouldDisableErrorScroll()) {
    setInvalidScrollPosition(invalidElements, scrollTrigger);
  }

  detail.srcEvent.preventDefault();
  errorCallback(invalidElements);
  triggerEvent($wrapper, error, updatedDetail);
};

/**
 * Performs the submit handler used for verifying the fields within an element.
 * @private
 * @param {Event} evt - DOM event
 * @param {jQuery} $wrapper - The element containing form elements
 * @param {Function} successCallback - Fired off if submission was successful
 * @param {Function} errorCallback - Fired off if submissions was unsuccessful
 * @param {Function} useCustomFormErrorScroll - Called on when an error has
 * been found
 * @param {Function} scrollTrigger - Called on to go a position on the page
 * @param {boolean} disableErrorScroll - Disables a form error from scrolling
 * to the error on the screen.
 */
const submitHandler = (
  evt,
  $wrapper,
  successCallback,
  errorCallback,
  useCustomFormErrorScroll,
  scrollTrigger,
  disableErrorScroll,
) => {
  forEachFormField($wrapper, validateField);

  const invalidElements = getInvalidElementList($wrapper);
  const validElements = getValidElementList($wrapper);

  verifyElementsValidity(invalidElements);

  let detail = {
    srcEvent: evt,
    validElements,
    validValues: createValuesMap(validElements),
    invalidElements,
    invalidValues: createValuesMap(invalidElements),
    pause() {
      evt.preventDefault();
    },
  };

  const processSubmissionFunc = processSubmission(
    $wrapper,
    successCallback,
    errorCallback,
    useCustomFormErrorScroll,
    scrollTrigger,
    disableErrorScroll,
  );

  detail = Object.assign({}, detail, {
    revalidate() {
      processSubmissionFunc(detail, {
        success: EVENTS.VALIDATOR_POST_SUCCESS,
        error: EVENTS.VALIDATOR_POST_ERROR,
      });
    },
    proceed() {
      processSubmissionFunc(detail, {
        success: EVENTS.VALIDATOR_POST_SUCCESS,
        error: EVENTS.VALIDATOR_POST_ERROR,
      });

      const updatedInvalidElements = getInvalidElementList($wrapper);
      if (!updatedInvalidElements.length) {
        $wrapper[0].submit();
      }
    },
  });

  processSubmissionFunc(detail, {
    success: EVENTS.VALIDATOR_SUCCESS,
    error: EVENTS.VALIDATOR_ERROR,
  });

  triggerEvent($wrapper, EVENTS.VALIDATOR_SUBMIT, Object.assign({}, detail, {
    hasErrors: !!invalidElements.length,
  }));
};

/**
 * Responsible for allowing a single handler to be called on to avoid a handler
 * to be called on multiple times.
 * @param {Function} handler - Func to call on
 * @returns {Function} An event callback
 */
const makeSingleEventCall = (handler) => {
  let called;
  return (evt) => {
    if (!called) {
      called = evt.type;
    }

    if (evt.type === called) {
      handler(evt);
    }
  };
};

/**
 * Based on the type of element, will apply an event listener whenever a value
 * has been finalized on the form element.
 * @private
 * @param {jQuery} $el - The form or container for form fields
 */
const listenForFieldChange = ($el) => {
  forEachFormField($el, (formField) => {
    const $formField = $(formField);
    const callback = () => {
      validateField(formField);
      formField.checkValidity();
    };

    $formField.on(createEvent('blur'), callback);

    if (formField.tagName.toLowerCase() === 'select') {
      $formField.on(createEvent('change'), callback);
    }
  });
};


/**
 * Responsible for setting a form element to not have native form validation.
 * Ideally, we would want to use native form validation. However, native form
 * validation does not meet the product/design requirements so we need to have
 * custom functionality for form validation.
 *
 * Here are some of the major differences between native form validation and
 * product requirements:
 *
 * Native Form Validation
 * - When an error is found upon submitting a form, no submit event is fired
 *   off. It's almost as though the browser is calling event.stopPropagation().
 * - Upon submitting the form, the browser will fire off an invalid event on
 *   the first found form field error.
 * - The first found error will be in focus
 *
 * Product Requirements:
 * - All fields must be validated upon submitting a form
 * - There are custom page position rules based on how the form has been
 *   configured
 *
 * An important thing to note, by setting novalidate, the form submission will
 * not be validated by the browser. However, the validation of the individual
 * fields can be performed independently from the native form validation, which
 * allows for this custom validation to occur.
 * @param {jQuery} $el - The container or form being validated
 */
const setNovalidate = ($el) => {
  const isForm = $el.prop('tagName').toLowerCase() === 'form';
  const hasValidateAttr = !!$el.data('validateNative');

  if (isForm && !hasValidateAttr) {
    $el.attr('novalidate', 'novalidate');
  }
};



/**
 * Validates fields within an element, most likely a form based on what is
 * passed in. The validation of fields is done by a couple of ways:
 *
 * 1. By using browser defaults around validity. For example, if a form element
 * is using a required attribute, has a type that uses built in validation, has
 * a patter attribute, or is controlling validity programmatically by
 * JavaScript.
 * 2. By leveraging data attributes that are coming from within. Think of these
 * attributes as additional configuration that extends the capabilities of
 * validation that isn't supported by the browser.
 * 3. A combination of two.
 *
 * Whether either of these conditions are met, they are finally triggered by
 * either a form submission or a click event applied to an element that
 * contains a data-validate-trigger attribute. This would allow for form
 * submissions to be applied to an element other than a input[type="submit"] or
 * button, in case it's needed. When any form elements are found invalid, a
 * pseudo class of invalid is set and can be styled by CSS.
 *
 * The goal of this work is to leverage as much from within the browser and
 * remove the need for as much custom work as possible.
 * @see validators
 * @see VALIDATOR_OPTIONS
 * @param {HTMLElement} el - An element containing fields used for validating
 * @param {Object} [options={}] - Options used for overriding
 */
export default function validator(el, options = {}, { deregister } = noopActions) {
  const $el = $(el);
  const $triggers = $el.find(`[${VALIDATE_TRIGGER}]`);
  const {
    validateOnlyOnSubmit,
    successCallback,
    errorCallback,
    scrollTrigger,
    useCustomFormErrorScroll,
    validateDisableErrorScroll: disableErrorScroll,
  } = Object.assign({}, VALIDATOR_OPTIONS, options, $el.data());

  setNovalidate($el);

  const partialSubmitHandler = partial(submitHandler,
    placeholder,
    $el,
    successCallback,
    errorCallback,
    useCustomFormErrorScroll,
    scrollTrigger,
    disableErrorScroll,
  );

  const submitHandlerFunc = makeSingleEventCall(partialSubmitHandler);

  $el.on(createEvent('submit'), submitHandlerFunc);

  if ($triggers.length) {
    $triggers.on(createEvent('click'), submitHandlerFunc);
  }

  if (!validateOnlyOnSubmit) {
    listenForFieldChange($el);
  }

  return {
    destroy: () => {
      $el.off(createEvent('submit'));
      $triggers.off(createEvent('click'));
      forEachFormField($el, (formField) => {
        $(formField)
          .off(createEvent('blur'))
          .off(createEvent('change'));

        const evt = new Event('valid');
        formField.dispatchEvent(evt);
        formField.setCustomValidity('');
        formField.checkValidity();
        deregister();
      });
    },
  };
}

registerJQueryPlugin(PLUGIN_NAME, validator);



// WEBPACK FOOTER //
// ./styleguide/src/assets/elements/fields/validator.js